import React, { RefObject } from 'react';
import { VideoOverlays } from './VideoOverlays';
import { CountryInfo } from '@/services/geolocation';
import { useRecordingProtectionOptional } from '@/contexts/RecordingProtectionContext';

type VideoType = 'local' | 'remote';
type PeerVideoStatus = 'waiting' | 'connecting' | 'reconnecting' | 'connected' | 'disconnected' | 'error';

interface VideoFeedProps {
  videoRef: RefObject<HTMLVideoElement>;
  videoType: VideoType;
  stream: MediaStream | null;
  filter: string;
  muted: boolean;
  mirror?: boolean;
  className?: string;
  videoClassName?: string;
  overlayState: {
    hasStream: boolean;
    sessionActive: boolean;
    peerVideoStatus?: PeerVideoStatus;
    isVideoEnabled?: boolean;
    isPeerVideoHidden?: boolean;
    peerVideoEnabled?: boolean;
    rtcConnectionState?: string;
    peerAudioEnabled?: boolean;
    swapPositions?: boolean;
    userCountry?: CountryInfo | null;
    peerCountry?: CountryInfo | null;
  };
  overlaySize?: 'full' | 'small';
  showVideo: boolean;
}

/**
 * Blackout overlay shown when screen recording is detected
 */
const RecordingBlackoutOverlay: React.FC<{ size: 'full' | 'small' }> = ({ size }) => {
  const isSmall = size === 'small';

  return (
    <div
      className="absolute inset-0 bg-black z-50 flex flex-col items-center justify-center select-none"
      style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
    >
      <div className={`text-white text-center ${isSmall ? 'px-2' : 'px-8'}`}>
        <svg
          className={`mx-auto mb-4 text-red-500 ${isSmall ? 'w-8 h-8' : 'w-16 h-16'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
          />
        </svg>
        <p className={`font-semibold ${isSmall ? 'text-xs' : 'text-lg'}`}>
          Screen recording detected
        </p>
        <p className={`text-gray-400 mt-1 ${isSmall ? 'text-xs' : 'text-sm'}`}>
          Video paused for privacy
        </p>
      </div>
    </div>
  );
};

/**
 * Reusable video feed component with overlays
 * Handles both local and remote video rendering with declarative overlays
 */
export const VideoFeed: React.FC<VideoFeedProps> = ({
  videoRef,
  videoType,
  stream,
  filter,
  muted,
  mirror = false,
  className = '',
  videoClassName = '',
  overlayState,
  overlaySize = 'full',
  showVideo
}) => {
  const videoId = videoType === 'local' ? 'selfVideo' : 'remoteVideo';
  const mirrorClass = mirror ? 'transform scale-x-[-1]' : '';
  const visibilityClass = showVideo ? 'opacity-100' : 'opacity-0 invisible';

  // Check for recording protection (optional - works even without provider)
  const recordingProtection = useRecordingProtectionOptional();
  const isRecordingDetected = recordingProtection?.isRecordingDetected ?? false;

  return (
    <div
      className={`w-full h-full relative video-protected ${className}`}
      onContextMenu={(e) => e.preventDefault()}
    >
      <video
        ref={videoRef}
        id={videoId}
        autoPlay
        playsInline
        muted={muted}
        style={{ filter }}
        className={`w-full h-full object-cover ${mirrorClass} ${visibilityClass} ${videoClassName}`}
        onLoadStart={() => console.log(`${videoType} video load started`)}
        onPlaying={() => console.log(`${videoType} video playing`)}
        onDragStart={(e) => e.preventDefault()}
        {...(videoType === 'remote' && {
          onPause: () => console.log('Video paused - no frames'),
          onSuspend: () => console.log('Video suspended - no frames'),
          onStalled: () => console.log('Video stalled - no frames'),
          onWaiting: () => console.log('Video waiting - buffering'),
          onEmptied: () => console.log('Video emptied - no source'),
          onEnded: () => console.log('Video ended'),
          onError: () => console.log('Video error'),
          onAbort: () => console.log('Video aborted')
        })}
      />
      {/* Recording blackout overlay - shown when screen capture detected */}
      {isRecordingDetected && <RecordingBlackoutOverlay size={overlaySize} />}
      <VideoOverlays
        videoType={videoType}
        state={overlayState}
        size={overlaySize}
      />
    </div>
  );
};
